import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";

/** The same shape check the database makes (migration 0084): a model name is an opaque string. */
const MODEL_SHAPE = /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/;

/**
 * The backend model a NEW business's receptionist starts on (platform_settings.default_agent_model, migration 0084). Changing it
 * does not touch existing businesses: each keeps its own model, set in the form below.
 */
export function DefaultAgentModel() {
  const [saved, setSaved] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from("platform_settings")
      .select("default_agent_model")
      .single()
      .then(({ data }: { data: { default_agent_model: string } | null }) => {
        if (data) {
          setSaved(data.default_agent_model);
          setDraft(data.default_agent_model);
        }
      });
  }, []);

  if (saved === null) return null;

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const model = draft.trim();
    if (!MODEL_SHAPE.test(model) || model.length > 100) return setError("Enter a model name such as gpt-6-luna (letters, digits and . _ : / - only, no spaces).");
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_admin_set_default_agent_model", { p_model: model });
    setBusy(false);
    if (err) return setError(err.message.includes("model_invalid") ? "That isn't a valid model name." : err.message);
    setSaved(model);
    setDraft(model);
    setMessage(`New businesses will start on ${model}. Existing businesses are unchanged.`);
  }

  return (
    <section style={{ border: "1px solid var(--rc-border)", borderRadius: 10, padding: 16, marginBottom: 20, maxWidth: 640 }}>
      <h2 style={{ fontSize: 16, margin: "0 0 4px" }}>Default model for new businesses</h2>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>
        The backend model a business's receptionist starts on when it is created. It does not change existing businesses; set each one's model below.
      </p>
      <form onSubmit={save} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          aria-label="Default model for new businesses"
          value={draft}
          maxLength={100}
          onChange={(e) => setDraft(e.target.value)}
          style={{ padding: 8, width: 260 }}
        />
        <button type="submit" disabled={busy || draft.trim() === saved} style={{ padding: "8px 16px" }}>
          {busy ? "Saving..." : "Save default"}
        </button>
      </form>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
      {message && <p style={{ color: "var(--rc-success)", fontSize: 12.5, margin: "8px 0 0" }}>{message}</p>}
    </section>
  );
}
